import { useEffect, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useWebSocket } from './use-web-socket';
import { SocketData, SocketStatusEnum } from 'src/types';
import { useFetchPvoData } from './use-fetch-pvo-data';
import { useDataContext } from 'src/contexts';

const FALLBACK_POLLING_INTERVAL_MS = 7000;

export const usePvoUploadStatus = () => {
  const { hash } = useParams();
  const navigate = useNavigate();
  const { fetchPvoData } = useFetchPvoData();
  const hasMedia = useDataContext((ctx) => ctx.hasMedia);

  const { isConnected, subscribe, unsubscribe } = useWebSocket();
  const [isLoading, setIsLoading] = useState(false);

  const uploadStatusChannel = `pvo.s3_uploads_status.${hash}`;

  const handleSocketMessage = useCallback(
    async (message: SocketData) => {
      const messageHash = message?.payload?.hash;
      const status = message?.payload?.status;

      console.log('[WebSocket]message', message);

      if (messageHash === hash) {
        unsubscribe(uploadStatusChannel);

        if (status === SocketStatusEnum.SUCCESS) {
          await fetchPvoData({ redirectOnError: false });
        } else if (status === SocketStatusEnum.ERROR) {
          setIsLoading(false);
          navigate('/error');
        }
      }
    },
    [hash, unsubscribe, uploadStatusChannel, fetchPvoData, navigate]
  );

  useEffect(() => {
    if (!isConnected || !hash) {
      return;
    }

    subscribe(uploadStatusChannel, handleSocketMessage);

    return () => {
      unsubscribe(uploadStatusChannel);
    };
  }, [isConnected, hash, subscribe, unsubscribe, handleSocketMessage]);

  useEffect(() => {
    if (!hash || hasMedia) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void fetchPvoData({ redirectOnError: false });
    }, FALLBACK_POLLING_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [hash, hasMedia, fetchPvoData]);

  return { isLoading };
};
